import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Briefcase, User, FileText, TrendingUp, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '@/utils/auth';
import api from '@/utils/api';

const JobSeekerDashboardHome = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [profileStatus, setProfileStatus] = useState({ 
    profile_completed: true, 
    profile_strength: 0,
    message: '', 
    missing_sections: [],
    suggestions: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, appsRes, statusRes] = await Promise.all([
        api.get(`/job-seeker/profile/${user.id}`),
        api.get(`/job-seeker/applications/${user.id}`),
        api.get(`/job-seeker/profile-status/${user.id}`)
      ]);
      setProfile(profileRes.data);
      setApplications(appsRes.data);
      setProfileStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const profileStrength = profileStatus.profile_strength || profile?.profile_strength || 0;
  const minimumRequired = 50;

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>Welcome, {user.name || profile?.full_name || 'Job Seeker'}!</h1>
      
      {/* Profile Completion Alert */}
      {!profileStatus.profile_completed && (
        <Alert className="mb-6 border-amber-300 bg-amber-50">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <div className="font-bold text-base mb-2">
              ⚠️ Profile {profileStrength}% Complete - Minimum 50% Required
            </div>
            <div className="text-sm mb-3">
              You need at least <strong>50% profile completion</strong> to apply for jobs and view employer contact details.
            </div>
            {profileStatus.missing_sections?.length > 0 && (
              <div className="text-sm mb-2">
                <strong>Complete these sections:</strong>
                <ul className="list-disc list-inside ml-2 mt-1">
                  {profileStatus.missing_sections.map((section, i) => (
                    <li key={i}>{section}</li>
                  ))}
                </ul>
              </div>
            )}
            {profileStatus.suggestions?.length > 0 && (
              <div className="text-sm text-amber-700 mb-2">
                <strong>Quick actions:</strong> {profileStatus.suggestions.slice(0, 3).join(' • ')}
              </div>
            )}
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white mt-2"
              onClick={() => navigate('/job-seeker/profile')}
            >
              Complete Profile Now →
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className={`p-6 border-slate-200 ${!profileStatus.profile_completed ? 'ring-2 ring-amber-400 bg-amber-50/30' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Profile Strength</h3>
            <TrendingUp className="w-5 h-5 text-teal-600" />
          </div>
          <div className="mb-4">
            {/* Progress bar with 50% marker */}
            <div className="relative">
              <div className="w-full bg-slate-200 rounded-full h-4">
                <div 
                  className={`h-4 rounded-full transition-all ${profileStrength >= minimumRequired ? 'bg-teal-600' : 'bg-amber-500'}`}
                  style={{width: `${profileStrength}%`}}
                />
              </div>
              {/* 50% marker line */}
              <div 
                className="absolute top-0 w-0.5 h-6 bg-slate-600 -translate-y-1"
                style={{left: '50%'}}
              />
              <span className="absolute text-xs text-slate-600 -translate-x-1/2" style={{left: '50%', top: '24px'}}>
                50% min
              </span>
            </div>
            <div className="flex items-baseline gap-2 mt-4">
              <p className="text-3xl font-bold text-slate-900">{profileStrength}%</p>
              {profileStrength >= minimumRequired ? (
                <span className="text-sm text-teal-600 font-medium">✓ Can apply for jobs</span>
              ) : (
                <span className="text-sm text-amber-600 font-medium">Need {minimumRequired - profileStrength}% more</span>
              )}
            </div>
            {!profileStatus.profile_completed && (
              <p className="text-sm text-amber-600 mt-2 font-medium">
                Complete your profile to at least 50% to apply for jobs
              </p>
            )}
          </div>
          <Button 
            data-testid="complete-profile-btn"
            onClick={() => navigate('/job-seeker/profile')} 
            variant={profileStatus.profile_completed ? "outline" : "default"}
            className={`w-full ${!profileStatus.profile_completed ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
          >
            {profileStatus.profile_completed ? 'Update Profile' : 'Complete Your Profile'}
          </Button>
        </Card>

        <Card className="p-6 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Applications</h3>
            <Briefcase className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-4xl font-bold text-slate-900 mb-2">{applications.length}</p>
          <p className="text-sm text-slate-600">Total job applications</p>
        </Card>
      </div>

      <Card className="p-6 border-slate-200">
        <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>Recent Applications</h3>
        {applications.length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600">You haven't applied to any jobs yet.</p>
            <Button onClick={() => navigate('/')} className="mt-4 bg-teal-600 hover:bg-teal-700">
              Browse Jobs
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {applications.slice(0, 5).map((app) => (
              <div key={app.id} className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-slate-900">{app.job_title}</h4>
                  <p className="text-sm text-slate-600">{app.company_name}</p>
                </div>
                <span className="text-sm text-slate-500">
                  {new Date(app.applied_at).toLocaleDateString('en-IN')}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default JobSeekerDashboardHome;